import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Mat } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save mat data.
 */
@Injectable()
export class MatService {
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all mats for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null, queryParams: string[] = null): Observable<Mat[]> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const mats: Mat[] = [];
                const bodyData: any = response.data || [];
                for (const mat of bodyData) {
                    mats.push((new Mat()).deserialize(mat));
                }
                return mats;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(
        eventId: string,
        matId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<Mat> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, matId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Mat().deserialize(response))));
    }

    /**
     * Add or update the mat
     */
    public save(mat: Mat, includes: string[] = null, queryParams: string[] = null): Observable<Mat> {
        queryParams = this.appendGetFields(queryParams);
        let response: Observable<IJsonApiResponse>;
        const matId: string = !mat.isNew ? mat.id : null;
        const apiRequestUrl: string = this.getApiUrl(mat.eventId, matId, includes, queryParams);

        if (!mat.isNew) {
            // Update existing Mat
            response = this.http.patch(apiRequestUrl, mat.serialize());
        } else {
            // Add a new Mat
            response = this.http.post(apiRequestUrl, mat.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new Mat()).deserialize(response)));
    }

    /**
     * Delete the specified mat.
     */
    public delete(eventId: string, matId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, matId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        matId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/mats/`;

        if (matId) {
            url += matId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[mat]') !== -1).length === 0) {
            queryParams.push(`fields[mat]=${Mat.getFields().join(',')}`);
        }

        return queryParams;
    }
}
